/**
 * Spanish elapsed-time prompts and chrome (ADR 0051 part 3).
 *
 * The sentence templates avoid a direct-object pronoun after "empezó"/
 * "terminó" ("empezó la lectura", not "la empezó") so no gendered agreement
 * has to track which activity noun is masculine or feminine - the same
 * reasoning PlaceValue and RoundingComparing's prompts use for their clauses.
 */
export const timeSentences = {
    duration: (name: string, activity: string, start: string, end: string) =>
        `${name} empezó ${activity} a las ${start}. Terminó a las ${end}. ¿Cuánto tiempo duró?`,
    end: (name: string, activity: string, start: string, durationText: string) =>
        `${name} empezó ${activity} a las ${start} y duró ${durationText}. ¿A qué hora terminó?`,
    start: (name: string, activity: string, end: string, durationText: string) =>
        `${name} terminó ${activity} a las ${end}, después de ${durationText}. ¿A qué hora empezó?`,
};

/** name/activity pairs; each activity carries its own article (la lectura, el paseo, …). */
export const sentenceSubjects: [string, string][] = [
    ['Sofía', 'la lectura'],
    ['Mateo', 'el entrenamiento de fútbol'],
    ['Valentina', 'la clase de piano'],
    ['Diego', 'la clase de arte'],
    ['Camila', 'el paseo en bicicleta'],
    ['Santiago', 'la clase de natación'],
    ['Isabella', 'el paseo'],
    ['Nicolás', 'el proyecto de ciencias'],
];

export const numberLinePrompts: Record<'duration' | 'end' | 'start', string> = {
    duration: '¿Cuánto tiempo pasó?',
    end: '¿A qué hora terminó?',
    start: '¿A qué hora empezó?',
};

export const timeChrome = {
    digitalPrompt: '¿Cuánto tiempo pasó? ____________',
    afterWord: 'después de',
    answerLabel: 'Respuesta:',
};

export function durationInWords(totalMinutes: number): string {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const hourPart = hours > 0 ? `${hours} hora${hours === 1 ? '' : 's'}` : '';
    const minutePart = minutes > 0 ? `${minutes} minuto${minutes === 1 ? '' : 's'}` : '';
    if (hourPart && minutePart) {
        return `${hourPart} ${minutePart}`;
    }
    return hourPart || minutePart || '0 minutos';
}

/** "a. m." / "p. m." with the space, as the RAE writes them; "mediodía" for 12:00 (ADR 0090 §8). */
export const dayHalves = { am: 'a. m.', pm: 'p. m.', noon: 'mediodía' };

/** The A.M.-or-P.M. event bank; same events and windows as English (ADR 0090 §2). */
export const amPmEvents: { text: string; half: 'am' | 'pm'; from: number; to: number }[] = [
    { text: 'Sofía desayuna', half: 'am', from: 6 * 60, to: 8 * 60 + 30 },
    { text: 'Mateo se despierta para ir a la escuela', half: 'am', from: 6 * 60, to: 7 * 60 + 30 },
    { text: 'El autobús escolar recoge a Valentina', half: 'am', from: 7 * 60, to: 8 * 60 + 30 },
    { text: 'Empiezan las clases de Diego', half: 'am', from: 8 * 60, to: 9 * 60 },
    { text: 'Sale el sol y canta el gallo', half: 'am', from: 5 * 60, to: 7 * 60 },
    { text: 'Camila pasea al perro antes de la escuela', half: 'am', from: 6 * 60 + 30, to: 8 * 60 },
    { text: 'Santiago tiene clase de matemáticas antes del almuerzo', half: 'am', from: 9 * 60, to: 10 * 60 + 30 },
    { text: 'Isabella cena', half: 'pm', from: 17 * 60, to: 19 * 60 },
    { text: 'Nicolás se va a la cama', half: 'pm', from: 19 * 60, to: 21 * 60 },
    { text: 'Salen las estrellas sobre la casa de Mateo', half: 'pm', from: 20 * 60, to: 22 * 60 },
    { text: 'Sofía merienda después de la escuela', half: 'pm', from: 15 * 60, to: 16 * 60 + 30 },
    { text: 'Terminan las clases de Valentina', half: 'pm', from: 14 * 60 + 30, to: 15 * 60 + 30 },
    { text: 'Diego lee un cuento antes de dormir', half: 'pm', from: 19 * 60, to: 20 * 60 + 30 },
    { text: 'Camila tiene entrenamiento de fútbol después de la escuela', half: 'pm', from: 15 * 60 + 30, to: 17 * 60 + 30 },
];

/** "Sofía desayuna a las 7:30." No window holds a 1 o'clock time, so "a las" is always right. */
export function amPmSentence(event: string, time: string): string {
    return `${event} a las ${time}.`;
}
